import { getPhoneCodesOptions } from "../../../../../../util/phone";
import type { TFunction } from "i18next";

export type AddressMode = "standard" | "advanced";

export type SelectOption = {
  label: string;
  value: string;
};

export type AddressValue = {
  address1?: string;
  city?: string;
  state?: string | SelectOption | null;
  country?: SelectOption | null;
  country_code?: string;
  country_name?: string;
  postal_code?: string;
  company_name?: string;
  address2?: string;
  address3?: string;
  state_display_name?: string;
  crm_id?: string;
  tax_id?: string;
  location_id?: string;
  poc_user_id?: string;
  poc_first_name?: string;
  poc_last_name?: string;
  poc_email?: string;
  poc_phone?: string;
  poc_country_code?: string | SelectOption;
};

// // Outgoing API payload
export type AddressApiPayload = {
  company_name?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  tax_id?: string;
  crm_id?: string;
  location_id?: string;
  poc_first_name?: string;
  poc_last_name?: string;
  poc_email?: string;
  poc_phone?: string;
  poc_country_code?: string;
};

export const EMPTY_ADDRESS = {
  company_name: "",
  address1: "",
  address2: "",
  address3: "",
  city: "",
  state: "",
  postal_code: "",
  tax_id: "",
  crm_id: "",
  location_id: "",
  poc_first_name: "",
  poc_last_name: "",
  poc_email: "",
  poc_phone: "",
};

export const getAddressTableColumns = (t: TFunction) => [
  { header: t("Attributes"), accessorKey: "attribute" },
];

export const getStandardAddressFields = (t: TFunction) => [
  { id: "1", attribute: t("Company name") },
  { id: "2", attribute: t("Country") },
  { id: "3", attribute: t("Address line 1") },
  { id: "4", attribute: t("Address line 2") },
  { id: "5", attribute: t("Address line 3") },
  { id: "6", attribute: t("State") },
  { id: "7", attribute: t("City") },
  { id: "8", attribute: t("Postal code") },
];

export const getAdvancedAddressFields = (t: TFunction) => [
  ...getStandardAddressFields(t),
  { id: "9", attribute: t("Tax ID") },
  { id: "10", attribute: t("CRM ID") },
  { id: "11", attribute: t("Location ID") },
  { id: "12", attribute: t("Point of contact") },
];

const normalizeSelectOption = (
  v: string | SelectOption | undefined | null
): string | undefined => {
  if (!v) return undefined;
  if (typeof v === "object" && "value" in v) return v.value || undefined;
  return v || undefined;
};

// Form state to API
export const transformAddress = (
  addr: AddressValue
): AddressApiPayload | null => {
  if (!addr || Object.keys(addr).length === 0) return null;

  const result: AddressApiPayload = {};

  const stringFields: (keyof AddressApiPayload)[] = [
    "company_name",
    "address1",
    "address2",
    "address3",
    "city",
    "postal_code",
    "tax_id",
    "crm_id",
    "location_id",
    "poc_first_name",
    "poc_last_name",
    "poc_email",
    "poc_phone",
  ];

  stringFields.forEach((key) => {
    const value = addr[key] as string | undefined;
    if (value) result[key] = value;
  });

  const countryCode =
    normalizeSelectOption(addr.country_code) ??
    normalizeSelectOption(addr.country);
  if (countryCode) result.country_code = countryCode;

  const state = normalizeSelectOption(addr.state);
  if (state) result.state = state;

  const pocCountryCode = normalizeSelectOption(addr.poc_country_code);
  if (pocCountryCode) result.poc_country_code = pocCountryCode;

  return Object.keys(result).length ? result : null;
};

// API response → form state
export const prepareAddressForForm = (addr: AddressValue): AddressValue => {
  const pocOptions = getPhoneCodesOptions();
  const countryCode = normalizeSelectOption(addr.country_code);
  const state = normalizeSelectOption(addr.state);

  const pocMatch = addr.poc_country_code
    ? pocOptions.find(
        (o) => o.value === normalizeSelectOption(addr.poc_country_code)
      ) ?? {
        label: normalizeSelectOption(addr.poc_country_code) ?? "",
        value: normalizeSelectOption(addr.poc_country_code) ?? "",
      }
    : null;

  return {
    ...addr,
    country: countryCode
      ? { label: addr.country_name ?? countryCode, value: countryCode }
      : null,
    state: state
      ? { label: addr.state_display_name ?? state, value: state }
      : null,
    poc_country_code: pocMatch ?? undefined,
  };
};
